import styled, { css } from "styled-components"
import { device } from "../styles/stylesConfig"
import { memo, useContext, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { allAnimations } from "../effects/Observers"
import { ThemeContext } from "../contexts/theme/ThemeContext"
import { useTranslation } from "react-i18next"

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    z-index: 2;
    border-bottom: 1px solid rgba(27, 48, 100, 0.3);
`

const ContentContainer = styled.div<{ type: string }>`
    width: 100%;
    display: grid;
    grid-template-columns: ${({ type }) => (type === 'left' ? '2fr 1fr' : '1fr 2fr')};
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 1;
    
    @media ${device.lg} {
        grid-template-columns: 1fr;
    }

`

const DataContainer = styled.div<{ type: string }>`
    order: ${({ type }) => (type === 'left' ? '1' : '2')};
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding-left: 10vw;
    padding-right: 5vw;
    padding-top: 15vh;
    padding-bottom: 15vh;

    @media ${device.lg} {
        padding-top: 10vh;
        padding-bottom: 10vh;
        padding-left: 5vw;
        order: unset;
    }
`

const ImageContainer = styled.div<{ type: string }>`
    order: ${({ type }) => (type === 'left' ? '2' : '1')};
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    background-color: #004bb5;
    margin-left: ${({ type }) => (type === 'left' ? '5vw' : '0')};
    margin-right: ${({ type }) => (type === 'right' ? '5vw' : '0')};
    padding-top: 15vh;
    padding-bottom: 15vh;
    position: relative;

    @media ${device.lg} {
        padding-top: 10vh;
        padding-bottom: 10vh;
        order: unset;
        margin: unset;
    }

    @media ${device.sm} {
    padding-top: 5vh;
    padding-bottom: 5vh;
    }
`

const Title = styled.h3<{ type: string }>`
    width: 100%;
    font-weight: bold;
    font-size: 48px;
    color: #2E2D2D;
    margin-bottom: 50px;
    text-align: ${({ type }) => (type === 'left' ? 'start' : 'end')};

    @media ${device.lg} {
        font-size: 38px;
    }

    @media ${device.md} {
        font-size: 28px;
    }

    @media ${device.sm} {
        font-size: 24px;
        text-align: center;
    }

    @media ${device.xs} {
        font-size: 18px;
    }

`

const Subtitle = styled.p<{ type: string }>`
    width: 100%;
    color: #2E2D2D;
    font-size: 20px;
    text-align: ${({ type }) => (type === 'left' ? 'start' : 'end')};
    z-index: 1;

    @media ${device.lg} {
        font-size: 16px;
    }

    @media ${device.md} {
        font-size: 14px;
    }

    @media ${device.sm} {
        font-size: 12px;
        width: auto;
        text-align: center;
    }

`

const ItemsContainer = styled.div<{ type: string }>`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 20px;
    margin: 30px 0px;

    @media ${device.sm} {
        align-items: center;
    }
`

const ItemDiv = styled.div<{ type: string }>`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: ${({ type }) => (type === 'left' ? 'row' : 'row-reverse')};
    gap: 10px;
    
    @media ${device.sm} {
        flex-direction: row;
        width: 180px;
    }
`

const ItemImg = styled.img`
    width: 30px;
    height: 30px;

    @media ${device.lg} {
        width: 25px;
        height: 25px;
    }

    @media ${device.md} {
        width: 20px;
        height: 20px;
    }

    @media ${device.sm} {
        width: 20px;
        height: 20px;
    }

    @media ${device.xs} {
        width: 20px;
        height: 20px;
    }
`

const Image = styled.img<{ type: string }>`
    width: 600px;
    height: 600px;
    object-fit: contain;
    transform: scaleX(-1);
    position: absolute;
    margin: auto;
    bottom: -50px;
    left: ${({ type }) => (type === 'left' ? '-200px' : '-100px')};
    top: ${({ type }) => (type === 'left' ? '0' : 'unset')};

    @media ${device.lg} {
        max-height: unset;
        width: 400px;
        height: 400px;
        bottom: 0px;
        left: ${({ type }) => (type === 'left' ? 'unset' : '-50px')};
        right: ${({ type }) => (type === 'left' ? '0px' : 'unset')};
        top: unset;
    }

    @media ${device.md} {
        width: 300px;
        height: 300px;
    }

        @media ${device.sm} {
        width: 150px;
        height: 150px;
        left: ${({ type }) => (type === 'left' ? 'unset' : '-20px')};

    }
`

interface Props {
    type: 'left' | 'right';
    title: string;
    image: any;
    descriptions: string[];
    items: string[];
    footer: string;
}

const GlassProductSectionComponent = ({ type, title, descriptions, image, items, footer }: Props) => {

    const location = useLocation()

    const {
        theme,
    } = useContext(ThemeContext);

    const { i18n } = useTranslation();

    useEffect(() => {

        allAnimations();

        return () => { allAnimations() };

    }, [location.pathname, i18n.language, theme])

    return (

        <Container>

            <ContentContainer type={type}>

                <DataContainer type={type}>

                    <Title className="utils__fade-in-fast-top" type={type}>{title}</Title>

                    {descriptions?.map((v) => (<Subtitle className="utils__fade-in-fast-top" type={type} key={v}>{v}</Subtitle>))}

                    <ItemsContainer type={type}>
                        {items?.map((v) => (
                            <ItemDiv className="utils__fade-in-fast-top" type={type} key={v}>
                                <ItemImg src={require('../images/checks.webp')} alt="Doble Checks" />
                                <Subtitle type={type}>{v}</Subtitle>
                            </ItemDiv>
                        ))}
                    </ItemsContainer>

                    <Subtitle className="utils__fade-in-fast-top" type={type}>{footer}</Subtitle>

                </DataContainer>

                <ImageContainer type={type}>

                    {image && <Image src={image} alt={title} type={type} loading="lazy" />}

                </ImageContainer>


            </ContentContainer>


        </Container>

    )
}

const GlassProductSection = memo(GlassProductSectionComponent);

export default GlassProductSection;